import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

interface PageResponse<TRaw> {
  data: TRaw[];
  meta: { count: number };
}

interface UseProgressiveLoadConfig<TRaw, TTransformed> {
  queryKey: unknown[];
  fetchPage: (page: number) => Promise<PageResponse<TRaw>>;
  transform: (raw: TRaw) => TTransformed;
}

interface UseProgressiveLoadResult<TTransformed> {
  data: TTransformed[];
  totalCount: number;
  isLoading: boolean;
  isFullyLoaded: boolean;
}

// AIDEV-NOTE: Generic progressive loading hook for SPA tables.
// Fetches page 1 immediately, then auto-fetches remaining pages in the background.
// When queryKey changes (e.g., filters applied), starts fresh with new data.
const useProgressiveLoad = <TRaw, TTransformed>(
  config: UseProgressiveLoadConfig<TRaw, TTransformed>,
): UseProgressiveLoadResult<TTransformed> => {
  const transformRef = useRef(config.transform);
  transformRef.current = config.transform;

  const query = useInfiniteQuery<PageResponse<TRaw>>({
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      const totalCount = lastPage.meta.count;

      if (totalFetched < totalCount) return allPages.length + 1;

      return undefined;
    },
    queryFn: ({ pageParam = 1 }) => config.fetchPage(pageParam),
    queryKey: config.queryKey,
    // Prevent refetching cached pages when switching back to a previously-loaded queryKey
    staleTime: 5 * 60 * 1000,
  });

  // Auto-fetch remaining pages in the background
  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const data = useMemo(
    () => (query.data?.pages ?? []).flatMap(page => page.data.map(transformRef.current)),
    [query.data],
  );

  const totalCount = query.data?.pages[0]?.meta?.count ?? 0;

  return {
    data,
    isFullyLoaded: !query.hasNextPage && !query.isFetchingNextPage,
    isLoading: query.isLoading,
    totalCount,
  };
};

export default useProgressiveLoad;
